import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText, Mail, ExternalLink, AlertCircle } from 'lucide-react';
import { useCurrentProducerTerms } from '@/hooks/useProducerTerms';
import ReactMarkdown from 'react-markdown';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';

export default function ProducerTermsLegalPage() {
  const { data: terms, isLoading, error } = useCurrentProducerTerms();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-muted/30 border-b">
          <div className="container max-w-4xl py-8 px-4">
            <Skeleton className="h-10 w-10 rounded-full" />
            <Skeleton className="h-8 w-64 mt-4" />
            <Skeleton className="h-4 w-48 mt-2" />
          </div>
        </header>
        <main className="container max-w-4xl py-8 px-4">
          <Card>
            <CardContent className="p-8 space-y-4">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-6 w-5/6" />
              <Skeleton className="h-6 w-2/3" />
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (error || !terms) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Villkoren kunde inte hämtas</h1>
            <p className="text-muted-foreground mb-4">
              Producentvillkoren är tillfälligt inte tillgängliga. Vänligen försök igen senare eller kontakta support.
            </p>
            <a 
              href="mailto:kontakt@farmly.se" 
              className="inline-flex items-center gap-2 text-primary hover:underline"
            >
              <Mail className="h-4 w-4" />
              kontakt@farmly.se
            </a>
          </CardContent>
        </Card>
      </div>
    );
  }

  const effectiveDate = terms.effective_from 
    ? format(new Date(terms.effective_from), 'd MMMM yyyy', { locale: sv })
    : null;

  // Extract section headers from markdown for table of contents
  const headings = terms.content_md
    .split('\n')
    .filter(line => line.startsWith('## '))
    .map(line => {
      const title = line.replace('## ', '').trim();
      const id = title
        .toLowerCase()
        .replace(/[^\wåäö\s]/g, '')
        .replace(/\s+/g, '-');
      return { id, title };
    });

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-muted/30 border-b">
        <div className="container max-w-4xl py-8 px-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="font-display text-2xl md:text-3xl font-medium">
                {terms.title}
              </h1>
              <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                <Badge variant="secondary">Version {terms.version}</Badge>
                {effectiveDate && <span>Gäller från: {effectiveDate}</span>}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container max-w-4xl py-8 px-4">
        <div className="grid lg:grid-cols-[220px_1fr] gap-8">
          {/* Table of Contents - Sticky sidebar */}
          {headings.length > 0 && (
            <aside className="hidden lg:block">
              <nav className="sticky top-8">
                <h2 className="font-medium text-sm text-muted-foreground mb-3 uppercase tracking-wide">
                  Innehåll
                </h2>
                <ul className="space-y-1.5 text-sm">
                  {headings.map((heading) => (
                    <li key={heading.id}>
                      <a
                        href={`#${heading.id}`}
                        className="text-muted-foreground hover:text-foreground transition-colors block py-1"
                      >
                        {heading.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>
          )}

          {/* Main content */}
          <Card className="shadow-sm">
            <CardContent className="prose prose-sm max-w-none p-6 md:p-8">
              {/* Mobile TOC */}
              {headings.length > 0 && (
                <details className="lg:hidden mb-6 bg-muted/50 rounded-lg p-4 not-prose">
                  <summary className="font-medium cursor-pointer">Innehållsförteckning</summary>
                  <ul className="mt-3 space-y-1.5 text-sm">
                    {headings.map((heading) => (
                      <li key={heading.id}>
                        <a
                          href={`#${heading.id}`}
                          className="text-primary hover:underline"
                        >
                          {heading.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {/* Markdown content */}
              <ReactMarkdown
                components={{
                  h1: ({ children }) => (
                    <h1 className="text-2xl font-semibold mt-0 mb-6">{children}</h1>
                  ),
                  h2: ({ children }) => {
                    const text = String(children);
                    const id = text
                      .toLowerCase()
                      .replace(/[^\wåäö\s]/g, '')
                      .replace(/\s+/g, '-');
                    return (
                      <>
                        <Separator className="my-6" />
                        <h2 id={id} className="text-lg font-semibold scroll-mt-8">
                          {children}
                        </h2>
                      </>
                    );
                  },
                  ul: ({ children }) => (
                    <ul className="space-y-1 list-disc pl-4">{children}</ul>
                  ),
                  li: ({ children }) => (
                    <li>{children}</li>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold">{children}</strong>
                  ),
                  hr: () => <Separator className="my-8" />,
                }}
              >
                {terms.content_md}
              </ReactMarkdown>

              <Separator className="my-8" />

              {/* Contact */}
              <section className="bg-muted/50 rounded-lg p-6 not-prose">
                <h2 className="font-semibold text-base mb-3">Kontakt</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Har du frågor om dessa villkor? Kontakta oss:
                </p>
                <a 
                  href="mailto:kontakt@farmly.se" 
                  className="inline-flex items-center gap-2 text-primary hover:underline text-sm"
                >
                  <Mail className="h-4 w-4" />
                  kontakt@farmly.se
                </a>
              </section>

              {/* Back link */}
              <div className="mt-8 pt-4 border-t not-prose">
                <a 
                  href="/" 
                  className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ExternalLink className="h-3 w-3" />
                  Tillbaka till Farmly
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-12">
        <div className="container max-w-4xl py-6 px-4">
          <p className="text-sm text-muted-foreground text-center">
            © {new Date().getFullYear()} Farmly AB. Alla rättigheter förbehållna.
          </p>
        </div>
      </footer>
    </div>
  );
}
